import { setCartTotalsUuid, setCFDScreen } from '../../VNCustomerFacingDisplay/ActionCreators'
import { DEVICE_MODEL } from '../../VNMode/Utils'
import { isDecimal } from '../../VNUtil/Number'
import { VN_BRIDGE_SEND_METHODS } from '../Enums'
import { isSDKCompatible } from '../Utils'
import { store } from '../../stores'
import VNDataBridgeReceiveType from './VNWebSDKDataReceive'
import { fakeSuccessResponse } from '../../utils/peripheralBridge'
import { setWebSDKDataReceive } from '../ActionCreators'
import { getDeviceMode } from '../../VNMode/Selectors'
import { MODES } from '../../VNMode/Reducer'
import { getPosDeviceType } from '../../selectors/peripheral'
import { POS_DEVICE_TYPES } from '../../utils/deviceData'

import { onBuildInfoReceived } from '../../actions/peripheral'
import { callHandlerPeripheralBridge } from '../../VNPeripheralBridge/Utils'
import { PERIPHERAL_BRIDGE_CALL_HANDLER_METHODS } from '../../VNPeripheralBridge/Enums'

const MIN_BUILD_INFO_BRIDGE_VERSION = '3.0.0'
/**
 * Post message to native bridge - Android only
 * @param {object} data
 */
const postMessageThroughBridge = (data) => {
  if (window.VN_DATA_BRIDGE && window.VN_DATA_BRIDGE !== undefined) {
    window.VN_DATA_BRIDGE.jsBridgePostMessage(JSON.stringify(data, replacer))
  }

  if (window.localStorage?.getItem('cfd_web_test_mode') === 'true') {
    window.Broadcaster?.postMessage({ type: 'MESSAGE', data: btoa(JSON.stringify(data, replacer)) })
  }

  // If Android, convert all values to string as this is a requirement
  // from the Android side
  function replacer(name, val) {
    if (Number.isInteger(val) || isDecimal(val) || typeof val == 'boolean') {
      return val.toString()
    } else {
      return val
    }
  }
}

/**
 * Messsage queue suppported by Android SDK
 * @link https://shift4.atlassian.net/wiki/spaces/venuenext/pages/2008843016
 */
const CFD_EPHEMERAL_TTL = 5 * 60 * 1000 // 5 minutes in milliseconds

const CFD_MAXIMUM_TTL = 60 * 60 * 0.5 * 1000 // 30 minutes in milliseconds

// Keep these in time order.
export const VNDataBridgeSendType = {
  VN_INITIALIZE_CFD: 'VN_INITIALIZE_CFD', // POS -> CFD
  VN_CFD_SCREEN_NAV: 'VN_CFD_SCREEN_NAV', // POS -> CFD
  VN_UPDATE_CART: 'VN_UPDATE_CART', // POS -> CFD
  VN_EXECUTE_SALES_REQUEST: 'VN_EXECUTE_SALES_REQUEST', // CFD -> ANDROID
  VN_EXECUTE_REFUND_REQUEST: 'VN_EXECUTE_REFUND_REQUEST', // POS -> ANDROID
  VN_CLEAR_APP_DATA: 'VN_CLEAR_APP_DATA', // ! CFD -> ANDROID ONLY
  VN_SEND_DEVICE_CONFIG: 'VN_SEND_DEVICE_CONFIG', // POS -> CFD
  VN_PRINT_RECEIPT: 'VN_PRINT_RECEIPT', // CFD -> ANDROID
  VNCFD_USER_PAYMENT_PROGRESS: 'VNCFD_USER_PAYMENT_PROGRESS', // CFD -> POS
  RETRY_PAYMENT: 'RETRY_PAYMENT', // POS -> CFD
  VN_ORDER_UPDATE: 'VN_ORDER_UPDATE', // CFD -> POS
  VN_GET_DEVICE_ID: 'VN_GET_DEVICE_ID',
  VN_RESET_CFD: 'VN_RESET_CFD', // POS -> CFD
  VN_RESET_DEVICE: 'VN_RESET_DEVICE', // POS -> ANDROID
  VN_EXECUTE_AUTH_REQUEST: 'VN_EXECUTE_AUTH_REQUEST',
  VN_PRINT_STATUS: 'VN_PRINT_STATUS', // POS -> CFD status of print / email job
  VN_CFD_WEB_VIEW_LOADED: 'VN_CFD_WEB_VIEW_LOADED', // CFD -> POS Tells the L1400 that the A3700 has loaded
  VN_RECEIPT_MESSAGE: 'VN_RECEIPT_MESSAGE', // message received receipt uuid
  VN_ENABLE_LOG_PASSTHROUGH: 'VN_ENABLE_LOG_PASSTHROUGH', // outgoing LOG
  VN_LOG_PASSTHROUGH: 'VN_LOG_PASSTHROUGH', // outgoing message
  VN_GET_BUILD_INFO: 'VN_GET_BUILD_INFO', // POS -> ANDROID
}

const MIN_CLEAR_DATA_VERSION = '2.18.3'

export const bridge_setCurrentOrder = (tip, charityAmountInCents) => {
  postMessageThroughBridge({
    method: VN_BRIDGE_SEND_METHODS.VN_BRIDGE_PASSTHROUGH,
    ttl: CFD_MAXIMUM_TTL,
    data: {
      method: VN_BRIDGE_SEND_METHODS.VN_SET_CURRENT_ORDER,
      data: { tip: tip, charityAmountInCents: charityAmountInCents },
    },
  })
}

export const bridge_updateTransactionLifecycle = (moment) => {
  postMessageThroughBridge({
    method: VN_BRIDGE_SEND_METHODS.VN_BRIDGE_PASSTHROUGH,
    ttl: CFD_MAXIMUM_TTL,
    data: {
      method: VN_BRIDGE_SEND_METHODS.VN_UPDATE_TRANSACTION_LIFECYCLE,
      data: { moment: moment },
    },
  })
}

export const bridge_updateCurrentPaymentFlow = (flow, messageUuid) => {
  postMessageThroughBridge({
    method: VN_BRIDGE_SEND_METHODS.VN_BRIDGE_PASSTHROUGH,
    ttl: CFD_MAXIMUM_TTL,
    data: {
      method: VN_BRIDGE_SEND_METHODS.VN_SET_CURRENT_PAYMENT_FLOW,
      data: { flow: flow },
      uuid: messageUuid,
    },
  })
}

export const bridge_enableLogPassthrough = (enabled) => {
  postMessageThroughBridge({
    method: VN_BRIDGE_SEND_METHODS.VN_BRIDGE_PASSTHROUGH,
    ttl: 0,
    data: {
      method: VNDataBridgeSendType.VN_ENABLE_LOG_PASSTHROUGH,
      data: enabled,
    },
  })
}

export const bridge_logPassthrough = (message) => {
  postMessageThroughBridge({
    method: VN_BRIDGE_SEND_METHODS.VN_BRIDGE_PASSTHROUGH,
    ttl: 0,
    data: {
      method: VNDataBridgeSendType.VN_LOG_PASSTHROUGH,
      data: message,
    },
  })
}

/**
 * Called when the POS has determined that an update is needed and the POS is going to refresh
 * And we need to refresh the CFD as well.
 */
export const bridge_updateCFDVersion = () => {
  postMessageThroughBridge({
    method: VN_BRIDGE_SEND_METHODS.VN_BRIDGE_PASSTHROUGH,
    ttl: CFD_MAXIMUM_TTL,
    data: {
      method: VN_BRIDGE_SEND_METHODS.VN_UPDATE_CFD_VERSION,
    },
  })
}

// ! Clear CFD app data, this will hopefully help with crashing
// ! Locked down to Android Version 2.18.3 and NEWER
export const CFDClearAppData = (androidSDKVersion) => {
  if (!isSDKCompatible(androidSDKVersion, MIN_CLEAR_DATA_VERSION)) return

  postMessageThroughBridge({
    method: VN_BRIDGE_SEND_METHODS.VN_CLEAR_APP_DATA,
    data: {},
  })
}

/**
 * L1400 -> A3700 the status of printing / sending email.
 * If an error occurs on the L1400 the A3700 should know about that and notify the customer
 */
export const bridge_sendPrintStatus = (error, message) => {
  const data = {
    error: error,
    message: message,
  }

  postMessageThroughBridge({
    method: VN_BRIDGE_SEND_METHODS.VN_BRIDGE_PASSTHROUGH,
    ttl: CFD_MAXIMUM_TTL,
    data: {
      method: VNDataBridgeSendType.VN_PRINT_STATUS,
      data: data,
    },
  })
}

/**
 * Current Device Model from Bridge
 *
 * @returns {string}
 */
export const bridge_getDeviceModel = () => {
  // calling bridge directly needs a function wrapper
  const callBridge = function () {
    return window.VN_DATA_BRIDGE?.getDeviceModel?.()
  }
  const deviceModel = callBridge()

  if (deviceModel) {
    return deviceModel
  }

  const userAgent = window?.navigator?.userAgent

  // fallback - identify by user agent
  if (userAgent?.includes?.(DEVICE_MODEL.A800)) {
    return DEVICE_MODEL.A800
  }

  if (userAgent?.includes?.(DEVICE_MODEL.E700)) {
    return DEVICE_MODEL.E700
  }

  if (userAgent?.includes?.(DEVICE_MODEL.A3700)) {
    return DEVICE_MODEL.A3700
  }

  if (!userAgent?.includes?.('Android')) {
    return DEVICE_MODEL.WEB
  }

  return ''
}

/**
 * Get the device ID from the Android SDK
 */
export const bridge_getDeviceId = () => {
  postMessageThroughBridge({
    method: VNDataBridgeSendType.VN_GET_DEVICE_ID,
  })
}

/**
 * Requests to reset the device with Android and do the registration all over again.
 */
export const bridge_resetDevice = () => {
  postMessageThroughBridge({
    method: VNDataBridgeSendType.VN_RESET_DEVICE,
  })
}

/**
 * Resets the CFD state back to it's initial state
 * there was an inital non-passthrough VN_RESET_CFD, but it's not operational
 *
 * @param {Boolean} resetOnlyPaymentsData
 */
export const resetCFDrequest = (keepCartData = false) => {
  const data = { keepCartData: keepCartData || undefined }

  postMessageThroughBridge({
    method: VN_BRIDGE_SEND_METHODS.VN_BRIDGE_PASSTHROUGH,
    typeId: `${VN_BRIDGE_SEND_METHODS.VN_BRIDGE_PASSTHROUGH}|${VNDataBridgeSendType.VN_RESET_CFD}`,
    data: {
      method: VNDataBridgeSendType.VN_RESET_CFD,
      data,
    },
    keepLatest: true,
    ttl: CFD_EPHEMERAL_TTL,
  })
}

export const bridge_sendExecuteAuthRequest = (data) => {
  postMessageThroughBridge({
    method: VN_BRIDGE_SEND_METHODS.VN_BRIDGE_PASSTHROUGH,
    ttl: CFD_MAXIMUM_TTL,
    data: {
      method: VNDataBridgeSendType.VN_EXECUTE_AUTH_REQUEST,
      data: data,
    },
  })
}

export const sendUpdateCart = (items, discounts) => {
  const validItems = items || []
  const validDiscounts = discounts || []

  const mappedDiscounts = validDiscounts.map((discount) => {
    discount.type = 'discount'
    return discount
  })
  const cartItems = [...validItems, ...mappedDiscounts]

  postMessageThroughBridge({
    method: VNDataBridgeSendType.VN_UPDATE_CART,
    data: {
      cartItems: cartItems,
    },
    typeId: VNDataBridgeSendType.VN_UPDATE_CART,
    keepLatest: true,
    ttl: CFD_EPHEMERAL_TTL,
  })
}

export const sendRetryPayment = (bridgePaymentMethod) => {
  postMessageThroughBridge({
    method: VNDataBridgeSendType.RETRY_PAYMENT,
    data: {
      bridgePaymentMethod,
    },
    typeId: VNDataBridgeSendType.RETRY_PAYMENT,
    keepLatest: true,
    ttl: CFD_EPHEMERAL_TTL,
  })
}

/**
 *
 * @param {String} screen specified CFD Screen, CFD_SCREEN Object in Reducer.js, VNCustomerFacingDisplay contains all of them
 * @param {Object} optionalData (OPTIONAL) Object with whatever data you need to pass with the keys you want.
 * {
 *  subtotal: '180',
 *  discount: '27',
 *  tax: '18',
 *  tip: '0',
 *  total: '196.65'
 * }
 */
export const sendCFDScreenNav = (screen, optionalData, messageUuid = null) => {
  let data = {
    screen: screen,
    ...optionalData,
    messageUuid,
  }

  // don't call if not in CFD
  const curState = store.getState()
  if (getDeviceMode(curState) !== MODES.POS || getPosDeviceType(curState) === POS_DEVICE_TYPES.E700) return

  store.dispatch(setCFDScreen(screen))

  if (optionalData?.cartTotals?.uuid) {
    store.dispatch(setCartTotalsUuid(optionalData?.cartTotals?.uuid))
  }

  postMessageThroughBridge({
    method: VNDataBridgeSendType.VN_CFD_SCREEN_NAV,
    typeId: VNDataBridgeSendType.VN_CFD_SCREEN_NAV,
    data: data,
    keepLatest: true,
    ttl: CFD_EPHEMERAL_TTL,
  })
}

export const isScreenAcceptable = (screen, cartTotalsUuid) => {
  postMessageThroughBridge({
    method: VNDataBridgeSendType.VN_CFD_SCREEN_NAV,
    typeId: VNDataBridgeSendType.VN_CFD_SCREEN_NAV,
    data: {
      screen: screen,
      cartTotalsUuid,
      onPair: true,
    },
    keepLatest: true,
  })
}

export const sendUserPaymentProgress = (data) => {
  postMessageThroughBridge({
    method: VNDataBridgeSendType.VNCFD_USER_PAYMENT_PROGRESS,
    typeId: VNDataBridgeSendType.VNCFD_USER_PAYMENT_PROGRESS,
    data: data,
    keepLatest: true,
    ttl: CFD_EPHEMERAL_TTL,
  })
}

export const sendExecuteAuthRequest = (data) => {
  if (window.localStorage?.getItem('cfd_web_test_mode') === 'true') {
    const successResponse = fakeSuccessResponse(
      data.uuid,
      data.orderNumber,
      data.hRef,
      data.amount,
      data.tipAmount,
      true,
      data.mcAnimationEnabled,
      data.deviceSerial,
      true,
    )
    setTimeout(() => {
      postMessageThroughBridge({
        method: VNDataBridgeReceiveType.VN_TRANSACTION_RESULT,
        data: successResponse,
      })

      store.dispatch(
        setWebSDKDataReceive({
          method: VNDataBridgeReceiveType.VN_TRANSACTION_RESULT,
          data: successResponse,
        }),
      )
    }, 1000)

    return
  }

  postMessageThroughBridge({
    method: VNDataBridgeSendType.VN_EXECUTE_AUTH_REQUEST,
    typeId: VNDataBridgeSendType.VN_EXECUTE_AUTH_REQUEST,
    data: data,
    keepLatest: true,
    ttl: CFD_EPHEMERAL_TTL,
  })
}

export const sendExecuteSalesRequest = (data) => {
  if (window.localStorage?.getItem('cfd_web_test_mode') === 'true') {
    const successResponse = fakeSuccessResponse(
      data.uuid,
      data.orderNumber,
      data.hRef,
      data.amount,
      data.tipAmount,
      true,
      data.mcAnimationEnabled,
      data.deviceSerial,
      true,
    )
    setTimeout(() => {
      postMessageThroughBridge({
        method: VNDataBridgeReceiveType.VN_TRANSACTION_RESULT,
        data: successResponse,
      })

      store.dispatch(
        setWebSDKDataReceive({
          method: VNDataBridgeReceiveType.VN_TRANSACTION_RESULT,
          data: successResponse,
        }),
      )
    }, 1000)

    return
  }

  postMessageThroughBridge({
    method: VNDataBridgeSendType.VN_EXECUTE_SALES_REQUEST,
    typeId: VNDataBridgeSendType.VN_EXECUTE_SALES_REQUEST,
    data: data,
    keepLatest: true,
    ttl: CFD_EPHEMERAL_TTL,
  })
}

/**
 * Intended for use with Card Present Refunds
 *
 * @link https://shift4.atlassian.net/wiki/spaces/venuenext/pages/1863221751
 * @param {Object} data
 */
export const sendExecuteRefundRequest = (data) => {
  postMessageThroughBridge({
    method: VNDataBridgeSendType.VN_EXECUTE_REFUND_REQUEST,
    typeId: VNDataBridgeSendType.VN_EXECUTE_REFUND_REQUEST,
    data,
    keepLatest: true,
    ttl: CFD_EPHEMERAL_TTL,
  })
}

/**
 * Intended for use with Card Present Refunds in CFD mode
 *
 * @link https://shift4.atlassian.net/wiki/spaces/venuenext/pages/1863221751
 * @param {Object} data
 */
export const sendPassthroughRefundRequest = (data) => {
  postMessageThroughBridge({
    method: VN_BRIDGE_SEND_METHODS.VN_BRIDGE_PASSTHROUGH,
    ttl: CFD_MAXIMUM_TTL,
    data: {
      method: VNDataBridgeSendType.VN_EXECUTE_REFUND_REQUEST,
      data,
    },
  })
}

/**
 * sent from cfd to pos in order to get the right order in state and stadium.
 * @param {Object} data
 */
export const sendOrderUpdate = (data) => {
  postMessageThroughBridge({
    method: VNDataBridgeSendType.VN_ORDER_UPDATE,
    data: data,
  })
}

export const sendPrintReceipt = (orderUuid, emails, phone) => {
  postMessageThroughBridge({
    method: VNDataBridgeSendType.VN_PRINT_RECEIPT,
    data: {
      orderUuid: orderUuid,
      emails: emails,
      phone: phone,
    },
  })
}

export const bridge_hijackedExecuteSalesRequest = (total, orderNumber, orderTotal, isFinalPayment = false, orderUuid) => {
  postMessageThroughBridge({
    method: VNDataBridgeSendType.VN_PRINT_RECEIPT,
    data: {
      TEMP_activateCardReader: true,
      total: total,
      orderNumber: orderNumber,
      uuid: orderUuid,
      isFinalPayment: isFinalPayment, // This is used for split pay where sometimes the receipt doesn't pop up because the A3700 is still processing,
      // but the command to go to the receipt screen was already sent by the L1400.
      orderTotal: orderTotal, // in the case that total is not the full order total and the receipt needs to
      // display the correct total
    },
  })
}

export const sendInitializeCFD = (data) => {
  postMessageThroughBridge({
    method: VNDataBridgeSendType.VN_INITIALIZE_CFD,
    keepLatest: true,
    typeId: VNDataBridgeSendType.VN_INITIALIZE_CFD,
    ttl: CFD_MAXIMUM_TTL,
    data: data,
  })
}

export const sendDeviceConfigCFD = (data) => {
  postMessageThroughBridge({
    method: VN_BRIDGE_SEND_METHODS.VN_BRIDGE_PASSTHROUGH,
    keepLatest: true,
    typeId: `${VN_BRIDGE_SEND_METHODS.VN_BRIDGE_PASSTHROUGH}|${VNDataBridgeSendType.VN_SEND_DEVICE_CONFIG}`,
    ttl: CFD_MAXIMUM_TTL,
    data: {
      method: VNDataBridgeSendType.VN_SEND_DEVICE_CONFIG,
      data: data,
    },
  })
}

export const sendCfdWebViewLoaded = (data) => {
  postMessageThroughBridge({
    method: VN_BRIDGE_SEND_METHODS.VN_BRIDGE_PASSTHROUGH,
    ttl: CFD_MAXIMUM_TTL,
    data: {
      method: VNDataBridgeSendType.VN_CFD_WEB_VIEW_LOADED,
      data: data,
    },
  })
}

/**
 * Ping with a Receipt Message UUID
 *
 * @param {uuid} messageUuid
 */
export const bridge_sendReceiptMessage = (messageUuid) => {
  postMessageThroughBridge({
    method: VN_BRIDGE_SEND_METHODS.VN_BRIDGE_PASSTHROUGH,
    ttl: CFD_MAXIMUM_TTL,
    data: {
      method: VNDataBridgeSendType.VN_RECEIPT_MESSAGE,
      uuid: messageUuid,
    },
  })
}

export const bridge_getBuildInfo = (androidSDKVersion = null, customCallback = null) => {
  const newBridgeMethod = () => {
    postMessageThroughBridge({
      method: VN_BRIDGE_SEND_METHODS.VN_GET_BUILD_INFO,
      ttl: CFD_MAXIMUM_TTL,
      data: {
        method: VN_BRIDGE_SEND_METHODS.VN_GET_BUILD_INFO,
      },
    })
  }

  // if version is set an compatible call new bridge
  if (androidSDKVersion && isSDKCompatible(androidSDKVersion, MIN_BUILD_INFO_BRIDGE_VERSION)) {
    newBridgeMethod()
    return
  }

  callHandlerPeripheralBridge(PERIPHERAL_BRIDGE_CALL_HANDLER_METHODS.GET_BUILD_INFO, null, (response) => {
    // TODO(mkramerl): remove this chained callback by adding custom callbacks to the new
    // bridge method
    newBridgeMethod()

    if (customCallback && customCallback instanceof Function) {
      customCallback(response)
      return
    }

    onBuildInfoReceived(response)
  })
}
